import type {
  GetChainFeeTokenDenomProps,
  GetCosmosRegistryChainRequest,
  GetEvmRegistryChainRequest,
} from '@polaris/core/models'
import { ChainType } from '@polaris/core/models'
import { type QueryClient, queryOptions } from '@tanstack/react-query'
import {
  getChainFeeTokenDenom,
  getChains,
  getCosmosRegistryChain,
  getEvmRegistryChain,
} from '@/src/services/chains'
import { getPreferredEndpointRecord } from '@/src/services/preferred-endpoint'

export const chainsQuery = () =>
  queryOptions({
    queryKey: ['chains', 'all'],
    queryFn: () => getChains(),
    staleTime: 30_000, // 30 seconds
  })

export const cosmosRegistryChainQuery = (
  props: GetCosmosRegistryChainRequest,
) =>
  queryOptions({
    queryKey: ['chains', 'registry', 'detail', 'cosmos', props],
    queryFn: () => getCosmosRegistryChain(props),
    staleTime: 60 * 60 * 24 * 1_000, // 1 day
  })

export const evmRegistryChainQuery = (props: GetEvmRegistryChainRequest) =>
  queryOptions({
    queryKey: ['chains', 'registry', 'detail', 'evm', props],
    queryFn: () => getEvmRegistryChain(props),
    staleTime: 60 * 60 * 24 * 1_000, // 1 day
  })

export const chainFeeTokenDenomQuery = (props: GetChainFeeTokenDenomProps) =>
  queryOptions({
    queryKey: ['chainFeeTokenDenom', props.chainId, props.chainType],
    queryFn: () => getChainFeeTokenDenom(props),
    enabled: Boolean(props.chainId && props.chainType),
    staleTime: Number.POSITIVE_INFINITY,
  })

/**
 * Query options for getting cosmos chains with bech32 prefixes
 * Uses ensureQueryData to share the supportedChains result
 */
export const cosmosChainPrefixesQuery = (queryClient: QueryClient) =>
  queryOptions({
    queryKey: ['chains', 'cosmos', 'prefixes'],
    queryFn: async () => {
      // Use ensureQueryData to get cached result or fetch if needed
      const { chains } = await queryClient.ensureQueryData(chainsQuery())
      const prefixes: Record<string, string> = {}

      for (const chain of chains) {
        if (
          chain.chain_type === ChainType.Cosmos &&
          chain.cosmos_data?.bech32_prefix
        ) {
          prefixes[chain.chain_id || ''] = chain.cosmos_data.bech32_prefix
        }
      }

      return prefixes
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })

/**
 * Query options for getting preferred RPC endpoint for a chain
 * Note: According to the API contract, each endpoint record should be used once
 * for RPS bandwidth accounting. Set staleTime to 0 to fetch fresh endpoints.
 */
export const preferredEndpointQuery = (chainId: string) =>
  queryOptions({
    queryKey: ['chains', 'preferred-endpoint', chainId],
    queryFn: () => getPreferredEndpointRecord({ chainId }),
    enabled: Boolean(chainId),
    staleTime: 0, // Always fetch fresh endpoint per API contract
    gcTime: 0, // Don't cache to ensure fresh endpoints
  })
